import { cn } from '@cobliteam/colib-ui/cn';
import type { MaturityAreaKey, MaturityNodeStatus } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import type { MaturityAreaState } from '@cobliteam/hooks/src/useBasicValueMaturity/useBasicValueMaturity';
import { useTranslation } from '@cobliteam/i18n';
import { tv } from 'tailwind-variants';
import { AREA_ICON } from '../basicValueMaturityPresentation';

interface MaturityTrackCompactProps {
  areas: MaturityAreaState[];
  /** Stage of the step currently shown in the modal — highlighted as the active node. */
  activeAreaKey?: MaturityAreaKey;
}

// State color follows the maturity status: locked = gray (not started), current = blue (in progress),
// done = green (completed). Each stage shows its own icon — no numbering, no connector lines.
const dotVariants = tv({
  base: 'grid size-11 place-items-center rounded-full border-2 transition-colors',
  variants: {
    status: {
      done: 'border-success bg-success text-success-foreground',
      current: 'border-primary bg-primary text-primary-foreground ring-4 ring-primary/15',
      locked: 'border-border bg-background text-muted-foreground',
    },
  },
});

const TrackNode = ({ area, active }: { area: MaturityAreaState; active: boolean }) => {
  const { t } = useTranslation('basic-value-onboarding');
  const AreaIcon = AREA_ICON[area.key];
  // The active step's stage always reads as "current"; otherwise use the area's own progress state
  // (não iniciado / em progresso / concluído).
  const status: MaturityNodeStatus = active ? 'current' : area.status;

  const stateText =
    status === 'done' ? t('track.state.done') : status === 'current' ? t('track.state.progress', { done: area.doneCount, total: area.totalCount }) : t('track.state.locked');

  return (
    <div className='flex flex-col items-center px-1.5 text-center'>
      <div className={dotVariants({ status })}>
        <AreaIcon size={20} />
      </div>
      <div className={cn('mt-2.5 font-display font-semibold text-sm', status === 'locked' ? 'text-muted-foreground' : status === 'current' ? 'text-primary' : 'text-foreground')}>
        {t(`areas.${area.key}.title`)}
      </div>
      <div className={cn('mt-0.5 text-xs', status === 'current' ? 'font-semibold text-primary' : 'text-muted-foreground')}>{stateText}</div>
    </div>
  );
};

/** Stage track for the guided modal — same look as the page track, highlighting the current step's stage. */
const MaturityTrackCompact = ({ areas, activeAreaKey }: MaturityTrackCompactProps) => {
  if (areas.length === 0) return null;

  return (
    <div className='grid' style={{ gridTemplateColumns: `repeat(${areas.length}, minmax(0, 1fr))` }}>
      {areas.map((area) => (
        <TrackNode key={area.key} area={area} active={area.key === activeAreaKey} />
      ))}
    </div>
  );
};

export default MaturityTrackCompact;
